import type { DateString, ID } from '../common';
import type { VehicleType } from './vehicleGroup';

// Gearbox options
export const GEARBOX_OPTIONS = {
  MANUAL: 'manual',
  AUTOMATIC: 'automatic',
} as const;

export type GearboxType = (typeof GEARBOX_OPTIONS)[keyof typeof GEARBOX_OPTIONS];

// Fuel options for cars (includes hybrid)
export const CAR_FUEL_OPTIONS = {
  GASOLINE: 'gasoline',
  DIESEL: 'diesel',
  ELECTRIC: 'electric',
  HYBRID: 'hybrid',
} as const;

export type CarFuelType = (typeof CAR_FUEL_OPTIONS)[keyof typeof CAR_FUEL_OPTIONS];

// Fuel options for motorcycles/ATVs/scooters (no hybrid; the backend rejects electric for ATVs)
export const MOTO_FUEL_OPTIONS = {
  GASOLINE: 'gasoline',
  DIESEL: 'diesel',
  ELECTRIC: 'electric',
} as const;

export type MotoFuelType = (typeof MOTO_FUEL_OPTIONS)[keyof typeof MOTO_FUEL_OPTIONS];

// Engine strokes options
export const ENGINE_STROKES_OPTIONS = {
  TWO_STROKE: '2T',
  FOUR_STROKE: '4T',
} as const;

export type EngineStrokesType =
  (typeof ENGINE_STROKES_OPTIONS)[keyof typeof ENGINE_STROKES_OPTIONS];

// Engine cooling options
export const ENGINE_COOLING_OPTIONS = {
  AIR_COOLED: 'air-cooled',
  LIQUID_COOLED: 'liquid-cooled',
} as const;

export type EngineCoolingType =
  (typeof ENGINE_COOLING_OPTIONS)[keyof typeof ENGINE_COOLING_OPTIONS];

// Car specifications
export type CarSpecs = {
  // Numeric fields
  engineCC?: number | null;
  passengers?: number | null;
  doors?: number | null;
  baggages?: number | null;
  airbagsNumber?: number | null;
  tankLiters?: number | null;
  // Enum fields
  gearbox?: GearboxType | null;
  fuel?: CarFuelType | null;
  // Boolean features
  sunroof: boolean;
  airCondition: boolean;
  radioUSB: boolean;
  abs: boolean;
  airbag: boolean;
  gpsNavigation: boolean;
  bluetooth: boolean;
};

// Motorcycle specifications
export type MotorcycleSpecs = {
  // Numeric fields
  riders?: number | null;
  horsepowerHP?: number | null;
  engineCC?: number | null;
  discBrakes?: number | null;
  tankLiters?: number | null;
  // Enum fields
  gearbox?: GearboxType | null;
  engineStrokes?: EngineStrokesType | null;
  engineCooling?: EngineCoolingType | null;
  fuel?: MotoFuelType | null;
  // Boolean features
  helmetCase: boolean;
  starter: boolean;
  abs: boolean;
  cbs: boolean;
  topBox: boolean;
};

// ATV/Quad specifications (extends Motorcycle with additional fields)
export type AtvSpecs = MotorcycleSpecs & {
  reverseGear: boolean;
  fourByFour: boolean;
};

// Scooter specifications (same as Motorcycle)
export type ScooterSpecs = MotorcycleSpecs;

// Union type for all specifications
export type VehicleSpecifications = CarSpecs | MotorcycleSpecs | AtvSpecs;

// Vehicle Model (catalog item)
export type VehicleModel = {
  id: ID;
  rentalId: ID;
  vehicleType: VehicleType;
  groupId: ID;
  brand?: string | null;
  model?: string | null;
  title?: string | null;
  description?: string | null;
  specifications: VehicleSpecifications;
  primaryImageUrl?: string | null;
  secondaryImageUrls: ReadonlyArray<string>;
  ordering: number;
  isActive: boolean;
  createdAt: DateString;
  updatedAt: DateString;
  // Populated relation (optional, when fetched with expand)
  group?: {
    id: ID;
    name: string;
    vehicleType: VehicleType;
  };
};

export type CreateVehicleModelInput = {
  vehicleType: VehicleType;
  groupId: ID;
  brand: string;
  model: string;
  title?: string | null;
  description?: string | null;
  specifications: VehicleSpecifications;
  ordering: number;
  isActive: boolean;
};

export type UpdateVehicleModelInput = Partial<CreateVehicleModelInput>;

// Global vehicle model (platform template). A Rental copies it into its own
// catalogue; the copy is independent of later template edits.
export type GlobalVehicleModel = {
  id: ID;
  vehicleType: VehicleType;
  brand: string;
  model: string;
  description?: string | null;
  specifications: VehicleSpecifications;
  primaryImageUrl?: string | null;
  secondaryImageUrls: ReadonlyArray<string>;
};

// The platform admin view adds activity and timestamps.
export type AdminGlobalVehicleModel = GlobalVehicleModel & {
  isActive: boolean;
  createdAt: DateString;
  updatedAt: DateString;
};

// Template list responses carry only these two fields.
export type GlobalVehicleModelListResponse = {
  data: Array<GlobalVehicleModel>;
  total: number;
};

export type AdminGlobalVehicleModelListResponse = {
  data: Array<AdminGlobalVehicleModel>;
  total: number;
};

type GlobalVehicleModelCreateBase = {
  brand: string;
  model: string;
  description?: string | null;
};

// POST carries no isActive (backend default) and no image fields.
export type CreateGlobalVehicleModelInput =
  | (GlobalVehicleModelCreateBase & { vehicleType: 'CAR'; specifications: CarSpecs })
  | (GlobalVehicleModelCreateBase & { vehicleType: 'MOTORCYCLE'; specifications: MotorcycleSpecs })
  | (GlobalVehicleModelCreateBase & { vehicleType: 'ATV_QUAD'; specifications: AtvSpecs })
  | (GlobalVehicleModelCreateBase & { vehicleType: 'SCOOTER'; specifications: ScooterSpecs });

// PATCH cannot change the type and always sends the complete specifications object.
export type UpdateGlobalVehicleModelInput = {
  brand?: string;
  model?: string;
  description?: string | null;
  specifications?: VehicleSpecifications;
  isActive?: boolean;
};

export type CreateVehicleModelFromGlobalInput = {
  groupId: ID;
  model?: string;
  isActive?: boolean;
};

// Default specifications for each vehicle type
export const DEFAULT_CAR_SPECS: CarSpecs = {
  sunroof: false,
  airCondition: false,
  radioUSB: false,
  abs: false,
  airbag: false,
  gpsNavigation: false,
  bluetooth: false,
};

export const DEFAULT_MOTORCYCLE_SPECS: MotorcycleSpecs = {
  helmetCase: false,
  starter: false,
  abs: false,
  cbs: false,
  topBox: false,
};

export const DEFAULT_ATV_SPECS: AtvSpecs = {
  ...DEFAULT_MOTORCYCLE_SPECS,
  reverseGear: false,
  fourByFour: false,
};

export const DEFAULT_SCOOTER_SPECS: ScooterSpecs = {
  ...DEFAULT_MOTORCYCLE_SPECS,
};
